// SaveQueuePanel.jsx
// IMPROVED: Better status handling, edited state, clearer actions

import React, { memo } from "react";
import {
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Pause,
  RefreshCw,
  Trash2,
  Package,
  Edit3,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";

const SaveQueuePanel = memo(
  ({
    queue,
    stats,
    isProcessing,
    isPaused,
    onPause,
    onResume,
    onRetry,
    onRemove,
    onClearCompleted,
    onClose,
  }) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: "text-amber-500",
        bg: "bg-amber-50",
        border: "border-amber-100",
        label: "Queued",
        description: "Waiting to be saved",
      },
      processing: {
        icon: Loader2,
        color: "text-blue-500",
        bg: "bg-blue-50",
        border: "border-blue-100",
        label: "Saving",
        description: "Currently saving...",
        spin: true,
      },
      completed: {
        icon: CheckCircle2,
        color: "text-emerald-500",
        bg: "bg-emerald-50",
        border: "border-emerald-100",
        label: "Saved",
        description: "Successfully saved",
      },
      failed: {
        icon: XCircle,
        color: "text-red-500",
        bg: "bg-red-50",
        border: "border-red-100",
        label: "Failed",
        description: "Failed to save",
      },
      edited: {
        icon: Edit3,
        color: "text-amber-500",
        bg: "bg-amber-50",
        border: "border-amber-100",
        label: "Edited",
        description: "Needs re-save",
      },
    };

    const hasFailedOrEdited = stats.failed > 0 || stats.edited > 0;

    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Panel */}
        <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Package size={18} className="text-gray-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Save Queue</h2>
                <p className="text-xs text-gray-500">
                  {stats.total} items • {stats.completed} saved
                  {hasFailedOrEdited && (
                    <span className="text-amber-600 ml-1">
                      • {stats.failed + stats.edited} need attention
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          {/* Stats Bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <StatBadge
              icon={Clock}
              count={stats.pending}
              color="amber"
              label="Queued"
            />
            <StatBadge
              icon={Loader2}
              count={stats.saving}
              color="blue"
              label="Saving"
              spin
            />
            <StatBadge
              icon={CheckCircle2}
              count={stats.completed}
              color="emerald"
              label="Saved"
            />
            <StatBadge
              icon={XCircle}
              count={stats.failed}
              color="red"
              label="Failed"
            />
            <StatBadge
              icon={Edit3}
              count={stats.edited}
              color="amber"
              label="Edited"
            />
          </div>

          {/* Warning Banner */}
          {hasFailedOrEdited && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100">
              <AlertTriangle
                size={16}
                className="text-amber-500 flex-shrink-0"
              />
              <p className="text-xs text-amber-700">
                Some products need attention. Click retry to save again.
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
            {isPaused ? (
              <button
                onClick={onResume}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100"
              >
                <Play size={14} />
                Resume
              </button>
            ) : (
              <button
                onClick={onPause}
                disabled={!isProcessing && stats.pending === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 disabled:opacity-50"
              >
                <Pause size={14} />
                Pause
              </button>
            )}
            <button
              onClick={onClearCompleted}
              disabled={stats.completed === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
            >
              <Trash2 size={14} />
              Clear Completed
            </button>
          </div>

          {/* Queue List */}
          <div className="flex-1 overflow-y-auto">
            {queue.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Package size={48} strokeWidth={1} className="mb-3" />
                <p className="text-sm font-medium">No items in queue</p>
                <p className="text-xs mt-1">Save products to see them here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {queue.map((item) => {
                  const config =
                    statusConfig[item.status] || statusConfig.pending;
                  const Icon = config.icon;

                  return (
                    <div
                      key={item.id}
                      className={`px-4 py-3 ${config.bg} border-l-4 ${config.border}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded-lg bg-white/50`}>
                          <Icon
                            size={16}
                            className={`${config.color} ${
                              config.spin ? "animate-spin" : ""
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 text-sm truncate">
                              {item.name}
                            </p>
                            <span
                              className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${config.bg} ${config.color} border ${config.border}`}
                            >
                              {config.label}
                            </span>
                          </div>

                          {/* Status description or error */}
                          {item.error ? (
                            <p className="text-xs text-red-600 mt-1 line-clamp-2">
                              {item.error}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {config.description}
                            </p>
                          )}

                          {/* Timestamp */}
                          {item.completedAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              {formatTime(item.completedAt)}
                            </p>
                          )}

                          {/* Action buttons for failed/edited items */}
                          {(item.status === "failed" ||
                            item.status === "edited") && (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => onRetry(item.id)}
                                className="flex items-center gap-1 px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-200 hover:bg-gray-50"
                              >
                                <RefreshCw size={12} />
                                Retry Save
                              </button>
                              <button
                                onClick={() => onRemove(item.id)}
                                className="flex items-center gap-1 px-2 py-1 text-gray-500 text-xs hover:text-red-600"
                              >
                                <Trash2 size={12} />
                                Remove
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Quick actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {item.status === "completed" &&
                            item.savedProductId && (
                              <button
                                onClick={() => {
                                  // Navigate to product detail
                                  window.open(
                                    `/products/${item.savedProductId}`,
                                    "_blank"
                                  );
                                }}
                                className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                title="View product"
                              >
                                <ExternalLink size={14} />
                              </button>
                            )}
                          {(item.status === "completed" ||
                            item.status === "failed") && (
                            <button
                              onClick={() => onRemove(item.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - Pause indicator */}
          {isPaused && (
            <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
              <div className="flex items-center gap-2 text-amber-700">
                <Pause size={16} />
                <span className="text-sm font-medium">Queue paused</span>
                <span className="text-xs text-amber-600 ml-auto">
                  Click Resume to continue
                </span>
              </div>
            </div>
          )}

          {/* Processing indicator */}
          {isProcessing && !isPaused && (
            <div className="px-4 py-2 bg-blue-50 border-t border-blue-100">
              <div className="flex items-center gap-2 text-blue-700">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-xs font-medium">
                  Saving {stats.saving} product{stats.saving !== 1 ? "s" : ""}
                  ...
                </span>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes slide-in {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          .animate-slide-in {
            animation: slide-in 0.2s ease-out;
          }
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
      </div>
    );
  }
);

SaveQueuePanel.displayName = "SaveQueuePanel";

// Stat Badge Component
const StatBadge = memo(({ icon: Icon, count, color, label, spin }) => {
  if (count === 0) return null;

  const colors = {
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${colors[color]}`}
    >
      <Icon size={12} className={spin ? "animate-spin" : ""} />
      <span>{count}</span>
      <span className="hidden sm:inline">{label}</span>
    </div>
  );
});

StatBadge.displayName = "StatBadge";

// Format time helper
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default SaveQueuePanel;
